import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { Button, Table, TableHeader, TableHead, TableRow, TableBody, TableCell, Skeleton, } from '@cargomatic/ui';
import { AlertCircle, ChevronRight } from 'lucide-react';
import { cn } from 'utils/ui/classes';
import COLUMNS from './JobListColumns';
const DRAYAGE = 'drayage';
const JobRow = (columns, row) => {
    return columns.map((column, index) => {
        const key = `${column.field}-${row._key || row._id}`;
        return (React.createElement(TableCell, { "data-testid": `div-4e0516cc-${index}`, key: key, className: column.className }, column.value(row)));
    });
};
const JobList = ({ context, ledgerId, ledgerType, jobs, lineItems, isLoading = true, isError, }) => {
    const { t } = useTranslation();
    const [showOptionalColumns, setShowOptionalColumns] = useState(false);
    const lineItemLookup = useMemo(() => {
        if (!lineItems || lineItems.length === 0)
            return {};
        const ledgerProperty = context === 'invoice' ? 'receivables' : 'payables';
        const isShorthaul = ledgerType !== DRAYAGE;
        return lineItems.reduce((data, job) => {
            var _a;
            data[job._id.toString()] = ((_a = job[ledgerProperty]) !== null && _a !== void 0 ? _a : []).map((item) => {
                return Object.assign(Object.assign({}, item), { needs_equipment: !isShorthaul && !item.equipment });
            });
            return data;
        }, {});
    }, [lineItems, context, ledgerType]);
    const columns = COLUMNS[ledgerType] || [];
    const visibleColumns = showOptionalColumns
        ? columns
        : columns.filter((col) => !col.optional);
    const flatRows = useMemo(() => {
        if (!jobs)
            return [];
        const sorted = sortBy(jobs, ['driverName', 'jobDeliverBy']);
        const ledgerProperty = context === 'invoice' ? 'invoice_id' : 'payout_id';
        return sorted.flatMap((job) => {
            const items = lineItemLookup[job.shipment_id] || [];
            const currentItems = items.filter((item) => item[ledgerProperty] === ledgerId);
            if (currentItems.length === 0) {
                return [Object.assign(Object.assign({}, job), { _lineItem: null, _key: job._id })];
            }
            return currentItems.map((item, idx) => (Object.assign(Object.assign({}, job), { _lineItem: item, _key: `${job._id}-${item._id || idx}` })));
        });
    }, [jobs, lineItemLookup, context, ledgerId]);
    const localizeTitles = (column) => {
        let display_text = t(column.title, {
            count: column === null || column === void 0 ? void 0 : column.count,
        });
        if (column.titleize)
            display_text = titleize(display_text);
        return (React.createElement(TableHead, { "data-testid": "p-be4e0d7d", key: column.field, className: column.className }, display_text));
    };
    if (!jobs && !isLoading)
        return null;
    return (React.createElement("div", { "data-testid": "div-7df63e75", className: "overflow-x-auto" },
        React.createElement(Table, null,
            React.createElement(TableHeader, null,
                React.createElement(TableRow, null,
                    visibleColumns.map((column) => localizeTitles(column)),
                    !isLoading && (React.createElement(TableHead, { className: "w-12" },
                        React.createElement(Button, { variant: "ghost", size: "sm", className: "h-8 w-8 p-0", onClick: () => setShowOptionalColumns(!showOptionalColumns), title: showOptionalColumns
                                ? t('HIDE_OPTIONAL_COLUMNS')
                                : t('SHOW_OPTIONAL_COLUMNS') },
                            React.createElement(ChevronRight, { className: cn('h-4 w-4 transition-transform', showOptionalColumns && 'rotate-90') })))))),
            React.createElement(TableBody, null, isError ? (React.createElement(TableRow, null,
                React.createElement(TableCell, { colSpan: visibleColumns.length + 1, className: "py-8" },
                    React.createElement("div", { className: "flex items-center justify-center gap-2 text-destructive" },
                        React.createElement(AlertCircle, { className: "h-4 w-4" }),
                        React.createElement("span", null, t('FAILED_TO_LOAD_LINE_ITEMS')))))) : isLoading && !isError ? (Array.from({ length: 5 }).map((_, i) => (React.createElement(TableRow, { key: `skel-${i}` }, Array.from({ length: visibleColumns.length || 6 }).map((_, j) => (React.createElement(TableCell, { key: `skel-${i}-${j}` },
                React.createElement(Skeleton, { className: "h-4 w-full" })))))))) : flatRows.length === 0 ? (React.createElement(TableRow, null,
                React.createElement(TableCell, { colSpan: visibleColumns.length + 1, className: "text-center text-muted-foreground py-8" }, t('NO_DATA_FOUND')))) : (flatRows.map((row, index) => (React.createElement(TableRow, { "data-testid": `div-644b2292-${index}`, key: row._key },
                JobRow(visibleColumns, row),
                React.createElement(TableCell, null)))))))));
};
export default JobList;
JobList.propTypes = {
    context: PropTypes.string.isRequired,
    ledgerType: PropTypes.string.isRequired,
    ledgerId: PropTypes.string.isRequired,
    jobs: PropTypes.array.isRequired,
    lineItems: PropTypes.array,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
};
